import isWithinRange from 'date-fns/is_within_range';
import _ from 'lodash';
import { categoriesMap, fallbackLanguage } from '../constants';
import { LanguageKeys } from '../schemas/localized-text';
import { IPeriod } from '../schemas/period';
import {
  IService,
  IServiceDocument,
  IServiceSingleLang,
  localizedFields,
} from '../schemas/service';
import { toSingleLangLocation } from './location';
import { getPriceForPeople } from './service-price';
import { toSingleLangTagsFrom } from './tag';
/**
 * Finds the period for `atDate`
 * @param service the service
 * @param atDate the date for which we have to find the period
 */
export function getCurrentPeriod(service: IService, atDate: Date): IPeriod | undefined {
  if (service.periods && !_.isEmpty(service.periods)) {
    const activePeriod = service.periods.find(p => isWithinRange(atDate, p.startDate, p.endDate));
    if (activePeriod) return activePeriod;
    return service.periods[0];
  }
  return undefined;
}

export function isAccommodation(service: Pick<IService, 'categories'>) {
  return service.categories.find(category => category.names.en === categoriesMap.accommodation);
}

export function isActivity(service: Pick<IService, 'categories'>) {
  return service.categories.find(category => category.names.en === categoriesMap.activity);
}

export function calculateServicePriceFromPricing(
  service: Pick<IService, 'basePrice' | 'pricing'>,
  adults: number,
  children: number,
  infants: number,
) {
  const { pricing } = service;
  const existingPricing = _.find(pricing, { adults, children, infants });
  if (existingPricing) return existingPricing.price;

  const commonPeopleCount = adults + children;
  const twoAdultsPricing = _.find(pricing, { adults: 2, children: 0 });
  const oneAdultPricing = _.find(pricing, { adults: 1, children: 0 });
  const twoAdultsMultiplier = Math.floor(commonPeopleCount / 2);
  const oneAdultMultiplier = commonPeopleCount % 2;

  const twoPeopleCost =
    (twoAdultsPricing && twoAdultsPricing.price) ||
    (oneAdultPricing && oneAdultPricing.price) ||
    getPriceForPeople(service.basePrice, { adultCount: 2 });
  const onePersonCost =
    (oneAdultPricing && oneAdultPricing.price) ||
    (twoAdultsPricing && twoAdultsPricing.price) ||
    getPriceForPeople(service.basePrice, { adultCount: 1 });
  const price = twoAdultsMultiplier * twoPeopleCost + oneAdultMultiplier * onePersonCost;
  return price;
}

export function toSingleLangService<T extends IService>(
  service: T,
  lang: LanguageKeys,
): IServiceSingleLang<T> {
  if (typeof ((service as unknown) as IServiceDocument).toObject === 'function') {
    throw new Error('service object can not be a mongoose document, it has to be a POJO');
  }
  const clonedService = _.cloneDeep(service);
  for (const fieldKey of localizedFields) {
    const localizedValue = _.get(clonedService, fieldKey);
    if (!localizedValue) continue;
    if (_.isArray(localizedValue)) {
      for (let i = 0; i < localizedValue.length; i++) {
        const element = localizedValue[i];
        if (element && _.isObject(element)) {
          _.set(clonedService, `${fieldKey}[${i}]`, element[lang] || element[fallbackLanguage]);
        }
      }
    } else if (_.isObject(localizedValue)) {
      _.set(clonedService, fieldKey, localizedValue[lang] || localizedValue[fallbackLanguage]);
    }
  }
  if (clonedService.tags) toSingleLangTagsFrom(clonedService.tags, lang);
  if (clonedService.categories) {
    for (const category of clonedService.categories) {
      if (category && _.isObject(category.names)) {
        _.set(category, 'names', category.names[lang] || category.names[fallbackLanguage]);
      }
    }
  }
  if (clonedService.media && _.isArray(clonedService.media) && !_.isEmpty(clonedService.media)) {
    for (const media of clonedService.media) {
      if (media.names && _.isObject(media.names)) {
        _.set(media, 'names', media.names[lang] || media.names[fallbackLanguage]);
      }
    }
  }
  if (clonedService.location) toSingleLangLocation(clonedService.location, lang);
  return (clonedService as unknown) as IServiceSingleLang<T>;
}
